import React from 'react';

import type { ReportPreviewColumn } from '@aline/common';
import { colorForQuestionOption, getIsManualReportPreviewColumn, GQL } from '@aline/common';
import { Chip, Typography } from '@mui/material';
import clsx from 'clsx';
import { Calendar, Check, Hash, ListCheck, ListChecks, Type } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import {
  ReportPlanExcludeButton,
  useReportPlanRowStyles,
} from 'src/components/ai/chat/AskUser/ReportPlanRow';
import {
  denseOptionChipGeometry,
  OptionItemChip,
} from 'src/editor/toolbars/right-panel/ai/extraction/reports/ExtractionQuestionOptionsManager';
import { makeStyles } from 'tss-react/mui';

const DATA_TYPE_ICONS = {
  [GQL.ExtractionQuestionType.Boolean]: Check,
  [GQL.ExtractionQuestionType.Date]: Calendar,
  [GQL.ExtractionQuestionType.Number]: Hash,
  [GQL.ExtractionQuestionType.SingleSelect]: ListCheck,
  [GQL.ExtractionQuestionType.MultiSelect]: ListChecks,
  [GQL.ExtractionQuestionType.Text]: Type,
} satisfies Record<GQL.ExtractionQuestionType, React.ComponentType<LucideProps>>;

const DATA_TYPE_LABELS = {
  [GQL.ExtractionQuestionType.Boolean]: 'Boolean',
  [GQL.ExtractionQuestionType.Date]: 'Date',
  [GQL.ExtractionQuestionType.Number]: 'Number',
  [GQL.ExtractionQuestionType.SingleSelect]: 'Single Select',
  [GQL.ExtractionQuestionType.MultiSelect]: 'Multi Select',
  [GQL.ExtractionQuestionType.Text]: 'Text',
} satisfies Record<GQL.ExtractionQuestionType, string>;

/** Enough to read the shape of the choices without the row swallowing the card. */
const MAX_VISIBLE_OPTIONS = 12;

const useStyles = makeStyles()(theme => ({
  columnInfo: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  /**
   * A column this one reads from. Same box as a dense option chip, but neutral
   * and unfilled, so a reference never reads as an allowed answer.
   */
  dependencyChip: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: denseOptionChipGeometry.borderRadius,
    fontSize: denseOptionChipGeometry.fontSize,
    fontWeight: 600,
    lineHeight: denseOptionChipGeometry.lineHeight,
    padding: theme.spacing(0.25, 0.75),
  },
  /**
   * Labels share one column so they line up, and wrapped values keep a hanging
   * indent instead of running back under the label.
   */
  metaGrid: {
    columnGap: theme.spacing(1),
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    rowGap: theme.spacing(0.5),
  },
  metaLabel: {
    color: theme.appColors.secondaryType,
    fontSize: 12,
    fontStyle: 'italic',
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
  },
  metaValue: {
    alignItems: 'center',
    color: theme.appColors.secondaryType,
    display: 'flex',
    flexWrap: 'wrap',
    fontSize: 12,
    gap: theme.spacing(0.5),
    minWidth: 0,
    wordBreak: 'break-word',
  },
  /** Unlabelled: it is the column's obvious default line, so it spans the grid. */
  questionText: {
    gridColumn: '1 / -1',
  },
}));

interface ReportPreviewColumnRowProps {
  column: ReportPreviewColumn;
  disabled: boolean;
  /** Left out of the report on approve, and struck through until then. */
  excluded: boolean;
  inputTitles: string[];
  onToggleExclude: () => void;
}

/**
 * One extraction column of the proposed plan, read-only. The only action
 * is exclusion: the card is a yes/no on the plan, and a user who wants a
 * different column sends a chat message rather than editing in place. A
 * Manual Entry column has no question to show; it is marked instead.
 */
export const ReportPreviewColumnRow = React.memo(function ReportPreviewColumnRow({
  column,
  disabled,
  excluded,
  inputTitles,
  onToggleExclude,
}: ReportPreviewColumnRowProps) {
  const { classes } = useStyles();
  const options = column.options ?? [];
  const Icon = DATA_TYPE_ICONS[column.type];
  const { classes: rowClasses } = useReportPlanRowStyles();
  const isExistingQuestion = column.existingQuestionId !== undefined;
  const isManual = getIsManualReportPreviewColumn(column);
  const visibleOptions = options.slice(0, MAX_VISIBLE_OPTIONS);
  const hiddenOptionCount = options.length - visibleOptions.length;

  return (
    <div className={rowClasses.row}>
      <div className={clsx(rowClasses.content, excluded && rowClasses.excludedContent)}>
        <AlineTooltip arrow placement="top" title={DATA_TYPE_LABELS[column.type]}>
          <div className={rowClasses.typeSlot}>
            <Icon />
          </div>
        </AlineTooltip>
        <div className={classes.columnInfo}>
          <Typography
            className={excluded ? rowClasses.excludedText : undefined}
            variant="subtitle2"
          >
            {column.title}
          </Typography>
          <div className={classes.metaGrid}>
            {!isManual && (
              <span
                className={clsx(
                  classes.metaValue,
                  classes.questionText,
                  excluded && rowClasses.excludedText
                )}
              >
                {column.questionText}
              </span>
            )}
            {options.length > 0 && (
              <ColumnMetaRow excluded={excluded} label="Options:">
                {visibleOptions.map((option, order) => (
                  <OptionItemChip
                    key={option}
                    dense
                    className={excluded ? rowClasses.excludedText : undefined}
                    option={{ color: colorForQuestionOption(order), label: option }}
                  />
                ))}
                {hiddenOptionCount > 0 && <span>{`+${hiddenOptionCount} more`}</span>}
              </ColumnMetaRow>
            )}
            {inputTitles.length > 0 && (
              <ColumnMetaRow excluded={excluded} label="Depends On:">
                {inputTitles.map(title => (
                  <span
                    key={title}
                    className={clsx(classes.dependencyChip, excluded && rowClasses.excludedText)}
                  >
                    {title}
                  </span>
                ))}
              </ColumnMetaRow>
            )}
          </div>
        </div>
      </div>
      {isManual && <Chip label="Manual Entry" size="small" variant="outlined" />}
      {isExistingQuestion && <Chip label="Existing" size="small" variant="outlined" />}
      {!disabled && (
        <div className={rowClasses.actions}>
          <ReportPlanExcludeButton excluded={excluded} onToggle={onToggleExclude} />
        </div>
      )}
    </div>
  );
});

/**
 * One labelled line of column metadata. It renders a label cell and a value
 * cell as siblings so every row shares the grid's label column.
 */
function ColumnMetaRow({
  children,
  excluded,
  label,
}: {
  children: React.ReactNode;
  excluded: boolean;
  label: string;
}) {
  const { classes } = useStyles();
  const { classes: rowClasses } = useReportPlanRowStyles();
  return (
    <>
      <span className={clsx(classes.metaLabel, excluded && rowClasses.excludedText)}>{label}</span>
      <span className={classes.metaValue}>{children}</span>
    </>
  );
}
